// src/pages/coach/Settings.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuthStore } from "../../Store/auth";
import { useNavigate } from "react-router-dom";
import { RefreshCcw, CreditCard, UploadCloud } from "lucide-react";

import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";
import { getCroppedImg } from "../../components/CoachSignup"; // re-use helper from signup
import { API_ORIGIN } from "../../config/api";

// ====== CONFIG ======
const API = API_ORIGIN;

// ====== HELPERS ======
const normalize = (x) => (Array.isArray(x) ? x : x?.$values ?? []);
const arr = (v) => (Array.isArray(v) ? v : normalize(v));

const daysBetween = (toISO) => {
  if (!toISO) return null;
  const now = new Date();
  const to = new Date(toISO);
  return Math.ceil((to.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

// Existing early access is shown only for coaches who already have it.
const deriveSubscription = (coach) => {
  if (!coach) {
    return {
      state: "none",
      label: "No Subscription",
      planLabel: "Free",
      daysLeft: null,
      expiresAt: null,
      isPaidStripe: false,
    };
  }

  const tier = coach.subscriptionTier ?? coach.subscriptionPlan ?? null;
  let planLabel = "Free";
  if (tier === 1 || tier === "Basic" || tier === "basic") {
    planLabel = "Legacy Basic";
  } else if (tier === 2 || tier === "Pro" || tier === "Standard" || tier === "pro") {
    planLabel = "Lead Access";
  }

  const rawStatus = coach.subscriptionStatus ?? coach.subscription_status ?? null;
  const s = String(rawStatus ?? "").toLowerCase();

  let state = "none";
  let label = "No Subscription";

  if (s === "1" || s === "active") {
    state = "active";
    label = "Active";
  } else if (s === "2" || s === "pastdue" || s === "past_due" || s === "past-due") {
    state = "past_due";
    label = "Past Due";
  } else if (s === "3" || s === "canceled" || s === "cancelled") {
    state = "canceled";
    label = "Canceled";
  } else if (s === "4" || s === "expired") {
    state = "expired";
    label = "Expired";
  }

  const expiresAt =
    coach.currentPeriodEndUtc ||
    coach.subscriptionExpiresAtUtc ||
    coach.subscriptionExpiryUtc ||
    null;

  const daysLeft = expiresAt ? daysBetween(expiresAt) : null;

  // Stripe paid subscription?
  const stripeSubId =
    coach.stripeSubscriptionId ||
    coach.stripe_subscription_id ||
    coach.subscriptionId ||
    null;
  const isPaidStripe = !!stripeSubId;

  // ✅ Early Access FREE if: no Stripe subscription + expiry in future
  const now = new Date();
  if (!isPaidStripe && expiresAt) {
    const exp = new Date(expiresAt);
    if (exp > now) {
      state = "free";
      planLabel = "Early Access";
      label = `Early Access (Free • ends ${formatDate(expiresAt)})`;
    } else {
      state = "expired";
      planLabel = "Free";
      label = "Free listing; lead access unavailable";
    }
  }

  return { state, label, planLabel, daysLeft, expiresAt, isPaidStripe };
};

const dataURLtoBlob = (dataURL) => {
  const [header, base64] = dataURL.split(",");
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new Blob([array], { type: mime });
};

export default function Settings() {
  const { token, coachId, logout } = useAuthStore();
  const navigate = useNavigate();

  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [coachRaw, setCoachRaw] = useState(null);

  // only editable fields from UI
  const [profile, setProfile] = useState({
    fullName: "",
    phoneNumber: "", // ✅ editable phone
    description: "",
    pricePerSession: "",
    countryId: "",
    cityId: "",
    areaId: "",
  });
  const [initialProfile, setInitialProfile] = useState(null);

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);

  const [errs, setErrs] = useState({});

  // image crop
  const [imagePreview, setImagePreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState("");
  const [croppedBlob, setCroppedBlob] = useState(null);

  const subscription = deriveSubscription(coachRaw);

  // ====== Load coach + dictionaries ======
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!coachId || !token) return;

      setLoading(true);
      setError("");

      try {
        const res = await axios.get(`${API}/api/Coaches/${coachId}`, auth);
        if (!mounted) return;

        const d = res.data;
        setCoachRaw(d);

        const next = {
          fullName: d.fullName || "",
          phoneNumber: d.phoneNumber || "",
          description: d.description || "",
          pricePerSession:
            d.pricePerSession !== undefined && d.pricePerSession !== null
              ? String(d.pricePerSession)
              : d.price !== undefined && d.price !== null
              ? String(d.price)
              : "",
          countryId:
            d.countryId !== undefined && d.countryId !== null ? String(d.countryId) : "",
          cityId: d.cityId !== undefined && d.cityId !== null ? String(d.cityId) : "",
          areaId: d.areaId !== undefined && d.areaId !== null ? String(d.areaId) : "",
        };

        setProfile(next);
        setInitialProfile(next);

        // countries
        const cnRes = await axios.get(`${API}/api/Country`, auth);
        if (!mounted) return;
        setCountries(normalize(cnRes.data));

        // cities
        if (next.countryId) {
          const ciRes = await axios.get(`${API}/api/City/country/${next.countryId}`, auth);
          if (!mounted) return;
          setCities(normalize(ciRes.data));
        }

        // areas
        if (next.cityId) {
          const arRes = await axios.get(`${API}/api/Area/city/${next.cityId}`, auth);
          if (!mounted) return;
          setAreas(normalize(arRes.data));
        }
      } catch (e) {
        if (e?.response?.status === 401) logout?.();
        console.error(e);
        setError("Failed to load settings.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [coachId, token, auth, logout]);

  const isDirtyFields = useMemo(() => {
    if (!initialProfile) return false;
    const keys = Object.keys(initialProfile);
    for (const k of keys) {
      if (String(profile[k] ?? "") !== String(initialProfile[k] ?? "")) {
        return true;
      }
    }
    return false;
  }, [profile, initialProfile]);

  const isDirty = isDirtyFields || !!croppedBlob;

  const setField = (name, value) => {
    setProfile((p) => ({ ...p, [name]: value }));
    setErrs((e) => ({ ...e, [name]: "" }));
    setSuccess("");
    setError("");
  };

  const onChange = async (e) => {
    const { name, value } = e.target;
    setField(name, value);

    if (name === "countryId") {
      setCities([]);
      setAreas([]);
      setField("cityId", "");
      setField("areaId", "");
      if (value) {
        try {
          const res = await axios.get(`${API}/api/City/country/${value}`, auth);
          setCities(normalize(res.data));
        } catch (er) {
          if (er?.response?.status === 401) logout?.();
        }
      }
    }

    if (name === "cityId") {
      setAreas([]);
      setField("areaId", "");
      if (value) {
        try {
          const res = await axios.get(`${API}/api/Area/city/${value}`, auth);
          setAreas(normalize(res.data));
        } catch (er) {
          if (er?.response?.status === 401) logout?.();
        }
      }
    }
  };

  // ====== IMAGE ======
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setError("Please select a valid image file.");
      return;
    }
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.7,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      });
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
      };
      reader.readAsDataURL(compressed);
      setError("");
      setSuccess("");
    } catch (err) {
      console.error(err);
      setError("Image processing failed.");
    }
  };

  const onCropComplete = (_, pixels) => {
    setCroppedAreaPixels(pixels);
  };

  const generateCroppedImage = async () => {
    if (!imagePreview || !croppedAreaPixels) {
      setError("Please adjust the crop area first.");
      return;
    }
    try {
      const dataUrl = await getCroppedImg(imagePreview, croppedAreaPixels);
      if (!dataUrl?.startsWith("data:image")) throw new Error("Invalid crop result");
      const blob = dataURLtoBlob(dataUrl);
      setCroppedBlob(blob);
      setCroppedImageUrl(URL.createObjectURL(blob));
      setSuccess("Profile image ready to save ✅");
      setError("");
    } catch (err) {
      console.error(err);
      setError("Cropping failed. Please try again.");
    }
  };

  // ====== VALIDATION ======
  const validate = () => {
    const e = {};
    if (!profile.fullName.trim()) e.fullName = "Full name is required.";
    if (!profile.phoneNumber.trim()) e.phoneNumber = "Phone number is required.";
    if (profile.pricePerSession !== "" && Number(profile.pricePerSession) < 0) {
      e.pricePerSession = "Price must be ≥ 0.";
    }
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  // ====== BUILD FULL DTO ======
  const buildFormData = () => {
    const fd = new FormData();

    const fullName = profile.fullName?.trim() || "";
    const description = profile.description || "";
    const price =
      profile.pricePerSession === "" || profile.pricePerSession === null
        ? 0
        : Number(profile.pricePerSession);

    const countryId = profile.countryId || "";
    const cityId = profile.cityId || "";
    const areaId = profile.areaId || "";

    const email = coachRaw?.email || coachRaw?.Email || "";
    const phoneNumber =
      profile.phoneNumber?.trim() || coachRaw?.phoneNumber || coachRaw?.PhoneNumber || "";
    const gender = coachRaw?.gender || coachRaw?.Gender || "";

    const categoryId =
      coachRaw?.categoryId ?? coachRaw?.CategoryId ?? coachRaw?.category?.id ?? 0;
    const specialityId =
      coachRaw?.specialityId ?? coachRaw?.SpecialityId ?? coachRaw?.speciality?.id ?? 0;

    const password = coachRaw?.password || coachRaw?.Password || "";

    fd.append("FullName", fullName);
    fd.append("Email", email);
    fd.append("PhoneNumber", phoneNumber);
    fd.append("Password", password);
    fd.append("Gender", gender);
    fd.append("Price", String(price));
    fd.append("Description", description);
    fd.append("CategoryId", String(categoryId));
    fd.append("SpecialityId", String(specialityId));
    fd.append("CountryId", countryId || "0");
    fd.append("CityId", cityId || "0");
    fd.append("AreaId", areaId || "0");

    if (croppedBlob) {
      fd.append("ProfileImage", croppedBlob, "profile.png");
    }

    return fd;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");

    if (!validate()) return;
    if (!isDirty) {
      setSuccess("No changes to save.");
      return;
    }

    setSaving(true);
    try {
      const formData = buildFormData();
      await axios.put(`${API}/api/Coaches/${coachId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess("Changes saved successfully ✅");
      setInitialProfile({ ...profile });
      if (croppedBlob && croppedImageUrl) {
        setCoachRaw((prev) => (prev ? { ...prev, profileImage: croppedImageUrl } : prev));
      }
      setCroppedBlob(null);
    } catch (err) {
      if (err?.response?.status === 401) logout?.();
      console.error(err);
      const apiMsg =
        err?.response?.data?.message || err?.response?.data?.error || "Failed to save.";
      setError(apiMsg + " ❌");
    } finally {
      setSaving(false);
    }
  };

  const manageSubscription = () => navigate("/dashboard/subscription");

  const countryName =
    arr(countries).find((c) => String(c.id) === String(profile.countryId))?.name || "";
  const cityName =
    arr(cities).find((c) => String(c.id) === String(profile.cityId))?.name || "";
  const areaName =
    arr(areas).find((a) => String(a.id) === String(profile.areaId))?.name || "";

  const locationLabel = [areaName, cityName, countryName].filter(Boolean).join(" • ");

  const isActiveOrFree =
    subscription.state === "active" || subscription.state === "past_due" || subscription.state === "free";

  // ====== UI ======
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl sm:text-3xl font-extrabold">Settings</h2>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border hover:bg-gray-50"
          type="button"
        >
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* SUBSCRIPTION BANNER ONLY */}
      {!isActiveOrFree && (
        <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 p-3 flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-rose-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-rose-800">Lead access locked</div>
            <div className="text-sm text-rose-700">
              Your public listing remains live. Subscribe to Lead Access to view and reply to client inquiries.
            </div>
          </div>
          <button
            onClick={manageSubscription}
            type="button"
            className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700"
          >
            Manage plan
          </button>
        </div>
      )}

      {/* CARD */}
      <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* HERO + AVATAR INSIDE BANNER */}
        <div className="relative">
          <div className="h-28 sm:h-32 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
          <div className="absolute inset-x-0 bottom-0 px-4 sm:px-6 pb-3">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              {/* Avatar inside banner */}
              <div className="shrink-0">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white overflow-hidden bg-gray-100">
                  {croppedImageUrl ? (
                    <img src={croppedImageUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : coachRaw?.profileImage ? (
                    <img src={coachRaw.profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-gray-200 text-xs">
                      No image
                    </div>
                  )}
                </div>
              </div>

              {/* identity in banner (read only) */}
              <div className="flex-1 min-w-0 bg-white/90 backdrop-blur rounded-xl px-3 py-3 sm:px-4 sm:py-3 shadow-sm">
                <div className="text-xs font-medium text-gray-500">Coach name</div>
                <div className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                  {profile.fullName || "Your full name"}
                </div>

                <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
                  {coachRaw?.email && (
                    <span className="inline-flex items-center rounded-full border bg-gray-50 px-3 py-1">
                      {coachRaw.email}
                    </span>
                  )}
                  {profile.phoneNumber && (
                    <span className="inline-flex items-center rounded-full border bg-gray-50 px-3 py-1">
                      {profile.phoneNumber}
                    </span>
                  )}
                  {locationLabel && (
                    <span className="inline-flex items-center rounded-full border bg-blue-50 text-blue-700 px-3 py-1">
                      {locationLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT UNDER BANNER */}
        <div className="p-4 sm:p-6 pt-8 sm:pt-10">
          {/* Image upload & crop */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="border rounded-xl p-3 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Profile photo
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Upload a clear photo of your face. This helps clients trust you more.
                  </p>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="block text-xs" />
                </div>
                {croppedBlob && (
                  <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    Image ready to save.
                  </div>
                )}
              </div>

              {imagePreview && (
                <div className="mt-4">
                  <div className="relative w-full h-64 rounded-2xl overflow-hidden bg-black/5">
                    <Cropper
                      image={imagePreview}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-3 text-xs">
                    <span className="text-gray-500 min-w-[80px]">Zoom</span>
                    <input
                      type="range"
                      min={0.8}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="w-full"
                    />
                    <button
                      type="button"
                      onClick={generateCroppedImage}
                      className="ml-2 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs hover:bg-black inline-flex items-center gap-1"
                    >
                      <UploadCloud className="w-3 h-3" />
                      Save crop
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* LEFT */}
            <div className="space-y-4">
              {/* FULL NAME INPUT */}
              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm sm:text-base"
                  name="fullName"
                  value={profile.fullName}
                  onChange={onChange}
                  placeholder="Your full name"
                />
                {errs.fullName && <p className="text-xs text-rose-600 mt-1">{errs.fullName}</p>}
              </div>

              {/* PHONE INPUT ✅ */}
              <div>
                <label className="block text-sm font-medium mb-1">Phone Number</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm sm:text-base"
                  name="phoneNumber"
                  value={profile.phoneNumber}
                  onChange={onChange}
                  placeholder="+971 5X XXX XXXX"
                />
                {errs.phoneNumber && <p className="text-xs text-rose-600 mt-1">{errs.phoneNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">About You</label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 min-h-[120px] text-sm"
                  name="description"
                  value={profile.description}
                  onChange={onChange}
                  placeholder="Tell clients about your experience, certifications, and coaching style."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Price per Session (AED){" "}
                  <span className="text-gray-400 text-xs">(leave empty to show “Request price”)</span>
                </label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  type="number"
                  min="0"
                  step="1"
                  name="pricePerSession"
                  value={profile.pricePerSession}
                  onChange={onChange}
                  placeholder="e.g., 250"
                />
                {errs.pricePerSession && <p className="text-xs text-rose-600 mt-1">{errs.pricePerSession}</p>}
              </div>
            </div>

            {/* RIGHT: location */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  name="countryId"
                  value={profile.countryId}
                  onChange={onChange}
                  disabled={!countries.length || loading}
                >
                  {!countries.length ? (
                    <option>Loading countries…</option>
                  ) : (
                    <>
                      <option value="">Select Country</option>
                      {countries.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  name="cityId"
                  value={profile.cityId}
                  onChange={onChange}
                  disabled={!profile.countryId || loading}
                >
                  {!profile.countryId ? (
                    <option>Choose country first…</option>
                  ) : !cities.length ? (
                    <option>Loading cities…</option>
                  ) : (
                    <>
                      <option value="">Select City</option>
                      {cities.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Area</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  name="areaId"
                  value={profile.areaId}
                  onChange={onChange}
                  disabled={!profile.cityId || loading}
                >
                  {!profile.cityId ? (
                    <option>Choose city first…</option>
                  ) : !areas.length ? (
                    <option>Loading areas…</option>
                  ) : (
                    <>
                      <option value="">Select Area</option>
                      {areas.map((a) => (
                        <option key={a.id} value={String(a.id)}>
                          {a.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>

          {/* FEEDBACK */}
          {success && (
            <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              {success}
            </div>
          )}
          {error && (
            <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
              {error}
            </div>
          )}
        </div>

        {/* STICKY SAVE BAR */}
        <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t p-3 flex items-center justify-between">
          <div className="text-xs text-gray-600">
            {isDirty ? "You have unsaved changes." : "All changes saved."}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (!initialProfile) return;
                setProfile({ ...initialProfile });
                setErrs({});
                setSuccess("");
                setError("");
                setImagePreview(null);
                setCroppedAreaPixels(null);
                setCroppedBlob(null);
                setCroppedImageUrl("");
              }}
              className="px-3 py-2 rounded-lg border hover:bg-gray-50 text-sm"
              disabled={!isDirty || saving}
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={!isDirty || saving}
              className={`px-4 py-2 rounded-lg text-white text-sm inline-flex items-center gap-2 ${
                !isDirty || saving
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              <UploadCloud className="w-4 h-4" />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
